import { animate, state, style, transition, trigger } from "@angular/animations";
import { Location, NgClass, NgTemplateOutlet } from "@angular/common";
import {
	AfterViewInit,
	Component,
	ElementRef,
	inject,
	Input,
	input,
	OnChanges,
	OnDestroy,
	OnInit,
	output,
	SimpleChanges,
	viewChild,
} from "@angular/core";
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";
import { CardComponent } from "common/card/card.component";
import { MutexService } from "common/concurrency/mutex.service";
import { AnchorScrollService } from "common/editor/anchor-scroll.service";
import { DescriptionEditorComponent } from "common/editor/description-editor.component";
import { DescriptionService, EditableDescription, PluginRegistry } from "common/editor/description.service";
import { EditorToDisplayDescriptionMap } from "common/editor/editor-to-display-description-map";
import { NonProcessEditorProperties } from "common/editor/plugin.service";
import { PreferencesService } from "core/preferences.service";
import { Subject, Subscription, SubscriptionLike } from "rxjs";
import { ButtonsBottomComponent } from "common/buttons/buttons-bottom.component";
import { ButtonComponent } from "common/buttons/button.component";
import { TranslateModule } from "@ngx-translate/core";
import { DescriptionViewComponent } from "./description-view.component";
import { takeUntil } from "rxjs/operators";

type DisplayDescription = stages.core.format.DisplayDescription;

@Component({
	selector: "stages-description",
	templateUrl: "./description.component.html",
	styleUrls: ["description.component.scss"],
	animations: [
		trigger("expandCollapseFromTop", [
			state("collapsed", style({ "margin-top": "{{contentHeight}}px" }), { params: { contentHeight: 0 } }),
			state("expanded", style({ "margin-top": "0px" })),
			transition("collapsed <=> expanded", animate("0.4s 0s cubic-bezier(0.4, 0, 0.2, 1)")),
		]),
	],
	imports: [
		ButtonsBottomComponent,
		ButtonComponent,
		NgClass,
		TranslateModule,
		DescriptionViewComponent,
		NgTemplateOutlet,
		DescriptionEditorComponent,
	],
})
export class DescriptionComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	private route = inject(ActivatedRoute);
	private location = inject(Location);
	private router = inject(Router);
	private mutexService = inject(MutexService);
	private preferencesService = inject(PreferencesService);
	private anchorScrollService = inject(AnchorScrollService);
	private editorToDisplayDescriptionMap = inject(EditorToDisplayDescriptionMap);
	private card = inject(CardComponent, { optional: true });

	descriptionId = "desc1";
	editorId = "edt1";
	renderedDescription!: string;
	toggleFunction: MenuItem;
	pluginRegistry!: PluginRegistry;
	anchorSubscription!: SubscriptionLike;
	fragmentSubscription!: Subscription;
	fragment?: string | null;
	protected editorActive = false;
	private _modifiable?: boolean;
	private destroy$ = new Subject<boolean>();

	readonly beanGuid = input<string>();

	readonly beanType = input.required<string>();

	readonly beanIdentity = input.required<string>();

	readonly beanId = input.required<string>();

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input()
	set modifiable(value: boolean | undefined) {
		this._modifiable = value;
	}

	get modifiable(): boolean | undefined {
		return this._modifiable;
	}

	readonly processTypeIdent = input<string>();

	readonly unsafe = input<boolean>();

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	displayDescription!: DisplayDescription;

	readonly beanSubtype = input<string | null>(null);

	readonly descriptionService = input.required<DescriptionService>();

	readonly workspaceId = input.required<string>();

	readonly pv = input.required<string>();

	readonly properties = input<NonProcessEditorProperties>();

	readonly afterSave = output<DescriptionComponent>();

	readonly descriptionEditor = viewChild.required(DescriptionEditorComponent);

	readonly descriptionSlideContainer = viewChild.required<ElementRef>("descriptionSlideContainer");

	private descriptionIdentifier!: string;
	private descriptionPreference: string = "descriptionExpanded";
	private oldState: "collapsed" | "expanded" | null = null;

	expanded: boolean = true;

	// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- TODO: Remove this comment and fix warnings!
	animationParams: {} = {};
	firstLoad: boolean = true;
	private saveEnabled = false;
	private _confirmationDialogAccepted = false;
	protected isLoading = false;

	constructor() {
		this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
			if (event instanceof NavigationStart) {
				this.editorToDisplayDescriptionMap.clear();
			}
		});
	}

	ngOnInit(): void {
		this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap) => {
			this.firstLoad = true;
			this.descriptionIdentifier = "description" + paramMap.get("identity")!;

			this.preferencesService.getPreference(this.descriptionPreference, {}).then((preferences: StringToUnknown) => {
				this.toggleFunction = {
					disabled: () => {
						return this.editorActive;
					},
					on: () => {
						this.toggleDescription();
					},
				};

				this.pluginRegistry = this.descriptionService().getPluginRegistry();

				if (this.card) {
					if (this.card.contentCollapsible === undefined) {
						this.card.contentCollapsible = !this.editorActive;
						this.card.showCollapseOption = !this.editorActive;
						this.card.resetHeight();
					}

					let preference = true;

					if (this.card.contentCollapsible && preferences[this.descriptionIdentifier] !== undefined) {
						preference = preferences[this.descriptionIdentifier] as boolean;
					}

					this.expanded = preference;
					this.card.expanded = preference;
					this.card.externalToggleFunctions()?.push(this.toggleFunction);
					this.card.menuItems = this.createMenuItems();

					this.renderedDescription = this.displayDescription.html;

					if ((!this.card.contentCollapsible || this.fragment) && !this.expanded) {
						this.expanded = true;
						this.card.expanded = true;
						this.storeState(this.expanded);
					}
				}
			});
		});

		this.fragmentSubscription = this.route.fragment.subscribe((f) => {
			this.fragment = f;
			if (f != null) {
				setTimeout(() => {
					this.anchorScrollService.scrollToFragment(f);
				}, 100);
			}
		});

		this.anchorSubscription = this.location.subscribe((locationChange) => {
			if (locationChange.type !== "hashchange") {
				return;
			}
			const url = locationChange.url!;
			const newFragment = this.anchorScrollService.getHashFromPath(url);
			if (!newFragment) {
				this.fragment = this.anchorScrollService.SCROLL_TO_TOP;
				return;
			}
			const oldPath = this.router.serializeUrl(this.router.createUrlTree(["."], { relativeTo: this.route }));
			const newPath = this.anchorScrollService.getPathWithoutHash(decodeURI(url));
			if (newPath === oldPath) {
				this.fragment = decodeURIComponent(newFragment);
			}
		});

		this.anchorScrollService.register();

		const storedDescriptionAndEditorConfig = this.editorToDisplayDescriptionMap.get(
			this.editorId,
			this.beanType(),
			this.beanId(),
		);
		if (storedDescriptionAndEditorConfig) {
			this.edit(storedDescriptionAndEditorConfig.description);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.beanGuid || changes.beanType || changes.beanId) {
			this.cancel();
		}
		if (changes.modifiable && this.card) {
			this.card.menuItems = this.createMenuItems();
		}
	}

	ngAfterViewInit(): void {
		this.setExpandCollapseFromTop();
	}

	ngOnDestroy(): void {
		if (this.editorActive && this.descriptionEditor().getDescription().viewingMode === "EDIT") {
			this.editorToDisplayDescriptionMap.set(
				this.editorId,
				this.beanType(),
				this.beanId(),
				this.descriptionEditor().getDescriptionAndEditorConfig(),
			);
		}

		if (this.fragmentSubscription) {
			this.fragmentSubscription.unsubscribe();
		}

		this.fragment = undefined;

		if (this.anchorSubscription) {
			this.anchorSubscription.unsubscribe();
		}
		this.anchorScrollService.unregister();
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	isEmpty(): boolean {
		return !this.displayDescription || this.displayDescription.html === "";
	}

	edit(description?: DisplayDescription): void {
		this.setEditorActive(true);
		this.fragment = undefined;
		this.anchorScrollService.deactivate();
		this.saveEnabled = false; // ST-32547: Disable save until description is loaded
		if (description) {
			// wait until the DescriptionEditorComponent has been created after the next check cycle
			setTimeout(() => {
				this.descriptionEditor().edit(description);
				this.saveEnabled = true;
			}, 0);
		} else {
			this.isLoading = true;
			this.descriptionService()
				.getDescription(this.beanType(), this.beanIdentity(), this.workspaceId(), this.pv())
				.then((editableDescription: EditableDescription) => {
					// ST-32547: If editing is canceled, before getDescription()-Promise is fulfilled, descriptionEditor is not available anymore.
					const descriptionEditor = this.descriptionEditor();
					if (descriptionEditor) {
						descriptionEditor.edit(editableDescription.description);
						this.saveEnabled = true;
						this.isLoading = false;
					}
				});
		}
	}

	cancel(): void {
		if (!this.editorActive) {
			return;
		}
		this.setEditorActive(false);
		this.descriptionEditor().cancel();
		this.anchorScrollService.reactivate();
	}

	async save(): Promise<void> {
		if (!this.editorActive) {
			return;
		}

		this.saveEnabled = false;
		this.isLoading = true;
		const updatedDescription = this.descriptionEditor().getDescription();
		await this.mutexService.invokeWithResult("saveDescription", async () => {
			const displayDescription = await this.descriptionService().putDescription(
				this.beanType(),
				this.beanIdentity(),
				this.workspaceId(),
				this.pv(),
				updatedDescription,
			);
			// If editing is canceled, before putDescription()-Promise is awaited, descriptionEditor is not available anymore.
			// Similar to ST-32547: Inline description edit causes internal error
			const descriptionEditor = this.descriptionEditor();
			if (descriptionEditor) {
				descriptionEditor.quit();
			}
			this.setEditorActive(false);
			this.displayDescription = displayDescription;
			this.anchorScrollService.reactivate();
			this.isLoading = false;
			this.afterSave.emit(this);
		});
	}

	setEditorActive(isEditorActive: boolean): void {
		if (!isEditorActive) {
			this.editorToDisplayDescriptionMap.delete(this.editorId, this.beanType(), this.beanId());
		}
		this.editorActive = isEditorActive;
		if (this.card) {
			this.card.showCollapseOption = !isEditorActive;
			this.card.menuItems = isEditorActive ? [] : this.createMenuItems();
		}
	}

	setExpandCollapseFromTop(): void {
		const targetState = this.expanded ? "expanded" : "collapsed";
		if (!this.oldState || targetState !== this.oldState) {
			this.oldState = targetState;
			const scrollHeight = (this.descriptionSlideContainer().nativeElement as HTMLDivElement).scrollHeight;
			this.animationParams = {
				value: targetState,
				params: {
					contentHeight: -30 - scrollHeight,
				},
			};
		}
	}

	storeState(expanded: boolean): void {
		this.preferencesService.getPreference(this.descriptionPreference, {}).then((oldPreferences: StringToBoolean) => {
			const newPreferences = { ...oldPreferences };

			newPreferences[this.descriptionIdentifier] = expanded;
			this.preferencesService.setPreference(this.descriptionPreference, newPreferences);
		});
	}

	toggleDescription(): void {
		this.expanded = !this.expanded;
		this.firstLoad = false;
		this.setExpandCollapseFromTop();
		this.storeState(this.expanded);
	}

	createMenuItems(): MenuItem[] {
		return [
			{
				name: "process.description.action.edit",
				iconClass: "ico ico-edit",
				disabled: this.editorActive || !this.modifiable,
				on: () => {
					this.edit();
				},
			},
		];
	}

	isSaveEnabled(): boolean {
		return this.beanType() === "aitemplate" ? this.saveEnabled && this._confirmationDialogAccepted : this.saveEnabled;
	}
}
